import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Search, Users, Wallet, Plus, Minus, X, Sparkles, Loader2, Archive, ArchiveRestore, Trash2,
  Download, Mail, MessageSquare,
} from "lucide-react";
import { statusLabel } from "@/lib/ordering";

function money(n) { return `$${Number(n || 0).toFixed(2)}`; }
function fmtDate(iso) { try { return new Date(iso).toLocaleDateString(); } catch { return "—"; } }

export default function Customers() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [marketingFilter, setMarketingFilter] = useState("all");
  const archive = async (c, archived) => {
    try { await api.patch(`/customers/${c.id}/archive`, { archived }); toast.success(archived ? "Customer archived" : "Customer restored"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const removeCustomer = async (c) => {
    if (!window.confirm(`Permanently delete ${c.name || c.email}? This cannot be undone.`)) return;
    try { await api.delete(`/customers/${c.id}`); toast.success("Customer deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustSign, setAdjustSign] = useState("+");
  const [adjustAmount, setAdjustAmount] = useState("");
  const [adjustReason, setAdjustReason] = useState("");
  const [adjustSaving, setAdjustSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/customers${showArchived ? "?include_archived=true" : ""}`)
      .then((r) => setList(r.data))
      .catch(() => toast.error("Couldn't load customers"))
      .finally(() => setLoading(false));
  }, [showArchived]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return list.filter((customer) => {
      const matchesSearch = !s
        || (customer.name || "").toLowerCase().includes(s)
        || (customer.email || "").toLowerCase().includes(s)
        || (customer.phone || "").toLowerCase().includes(s);
      const matchesMarketing = marketingFilter === "all"
        || (marketingFilter === "email" && customer.email_marketing_opt_in)
        || (marketingFilter === "sms" && customer.sms_marketing_opt_in)
        || (marketingFilter === "none"
          && !customer.email_marketing_opt_in
          && !customer.sms_marketing_opt_in);
      return matchesSearch && matchesMarketing;
    });
  }, [list, marketingFilter, q]);

  const downloadAudience = async (channel) => {
    try {
      const { data } = await api.get("/marketing/audience", { params: { channel } });
      const rows = [
        ["Name", "Email", "Phone", "Consent updated"],
        ...data.customers.map((customer) => [
          customer.name || "",
          customer.email || "",
          customer.phone || "",
          customer.marketing_consent_updated_at || "",
        ]),
      ];
      const csv = rows
        .map((row) => row.map((value) => `"${String(value).replaceAll('"', '""')}"`).join(","))
        .join("\n");
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `sandar-sushi-${channel}-marketing-audience.csv`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(`${data.count} eligible ${channel} subscribers exported`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Couldn't create audience list");
    }
  };

  const openDetail = async (id) => {
    setOpenId(id); setLoadingDetail(true); setDetail(null);
    try {
      const { data } = await api.get(`/customers/${id}`);
      setDetail(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load customer");
      setOpenId(null);
    } finally { setLoadingDetail(false); }
  };
  const closeDetail = () => { setOpenId(null); setDetail(null); };

  const submitAdjust = async () => {
    const amt = parseFloat(adjustAmount);
    if (!amt || amt <= 0) { toast.error("Enter a positive amount"); return; }
    const signed = adjustSign === "-" ? -amt : amt;
    setAdjustSaving(true);
    try {
      await api.post(`/customers/${openId}/gift-card`, { amount: signed, reason: adjustReason });
      toast.success(`Gift card ${signed > 0 ? "credited" : "debited"} ${money(Math.abs(signed))}`);
      setAdjustOpen(false); setAdjustAmount(""); setAdjustReason(""); setAdjustSign("+");
      // Refresh detail + list
      await openDetail(openId);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Adjustment failed");
    } finally { setAdjustSaving(false); }
  };

  return (
    <div className="p-8 lg:p-10">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="eyebrow mb-2">People</p>
          <h1 className="font-display font-bold text-3xl tracking-tight" data-testid="admin-customers-title">Customers</h1>
          <p className="text-sm text-neutral-500 mt-1">{list.length} total · manage gift card balances and see order history</p>
        </div>
        <div className="relative w-full sm:w-80 flex items-center gap-2">
          <label className="text-xs text-neutral-500 whitespace-nowrap inline-flex items-center gap-1"><input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="customers-show-archived"/> Archived</label>
          <Search className="absolute left-[88px] top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400"/>
          <Input
            placeholder="Search by name, email, or phone…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-10"
            data-testid="customers-search"
          />
        </div>
      </div>

      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-sm font-medium text-neutral-700" htmlFor="customers-marketing-filter">
            Marketing consent
          </label>
          <select
            id="customers-marketing-filter"
            value={marketingFilter}
            onChange={(event) => setMarketingFilter(event.target.value)}
            className="h-9 rounded-lg border border-neutral-200 bg-white px-3 text-sm"
            data-testid="customers-marketing-filter"
          >
            <option value="all">All customers</option>
            <option value="email">Email subscribers</option>
            <option value="sms">Text subscribers</option>
            <option value="none">No marketing consent</option>
          </select>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => downloadAudience("email")}
            data-testid="export-email-audience"
          >
            <Mail className="mr-1 h-3.5 w-3.5" />
            Email audience
            <Download className="ml-1 h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => downloadAudience("sms")}
            data-testid="export-sms-audience"
          >
            <MessageSquare className="mr-1 h-3.5 w-3.5" />
            Text audience
            <Download className="ml-1 h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-neutral-200/70 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="p-4">Customer</th>
              <th className="p-4">Phone</th>
              <th className="p-4">Joined</th>
              <th className="p-4 text-right">Orders</th>
              <th className="p-4 text-right">Spend</th>
              <th className="p-4 text-right">Gift Card</th>
              <th className="p-4">Marketing</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {loading ? (
              <tr><td colSpan={8} className="p-10 text-center text-neutral-500">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={8} className="p-10 text-center text-neutral-500 flex flex-col items-center gap-2">
                <Users className="w-8 h-8 text-neutral-300"/> No customers found.
              </td></tr>
            ) : filtered.map((c) => (
              <tr key={c.id} className="hover:bg-neutral-50" data-testid={`customer-row-${c.id}`}>
                <td className="p-4">
                  <div className="font-medium">{c.name || "—"}</div>
                  <div className="text-xs text-neutral-500">{c.email}</div>
                </td>
                <td className="p-4 text-neutral-600">{c.phone || "—"}</td>
                <td className="p-4 text-neutral-500 text-xs">{fmtDate(c.created_at)}</td>
                <td className="p-4 text-right font-medium">{c.orders_count}</td>
                <td className="p-4 text-right">{money(c.lifetime_spend)}</td>
                <td className="p-4 text-right">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                    (c.gift_card_balance || 0) > 0 ? "bg-emerald-100 text-emerald-700" : "bg-neutral-100 text-neutral-500"
                  }`}>
                    <Wallet className="w-3 h-3"/> {money(c.gift_card_balance)}
                  </span>
                </td>
                <td className="p-4">
                  <div className="flex flex-wrap gap-1" data-testid={`customer-marketing-${c.id}`}>
                    {c.email_marketing_opt_in && (
                      <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                        Email
                      </span>
                    )}
                    {c.sms_marketing_opt_in && (
                      <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                        Text
                      </span>
                    )}
                    {!c.email_marketing_opt_in && !c.sms_marketing_opt_in && (
                      <span className="text-xs text-neutral-400">Not subscribed</span>
                    )}
                  </div>
                </td>
                <td className="p-4 text-right whitespace-nowrap">
                  {c.archived && <span className="text-[10px] uppercase tracking-wider bg-neutral-200 text-neutral-600 rounded px-1.5 py-0.5 mr-2">archived</span>}
                  <Link to={`/admin/customers/${c.id}`}><Button size="sm" className="rounded-full mr-1 bg-neutral-900" data-testid={`customer-profile-${c.id}`}>Profile</Button></Link>
                  <Button size="sm" variant="outline" onClick={() => archive(c, !c.archived)} className="rounded-full mr-1" title={c.archived ? "Restore" : "Archive"} data-testid={`customer-archive-${c.id}`}>{c.archived ? <ArchiveRestore className="w-3.5 h-3.5"/> : <Archive className="w-3.5 h-3.5"/>}</Button>
                  <Button size="sm" variant="outline" onClick={() => removeCustomer(c)} className="rounded-full mr-1 text-red-600" title="Delete" data-testid={`customer-delete-${c.id}`}><Trash2 className="w-3.5 h-3.5"/></Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openDetail(c.id)}
                    className="rounded-full"
                    data-testid={`customer-open-${c.id}`}
                  >View</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!openId} onOpenChange={(v) => !v && closeDetail()}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {loadingDetail ? "Loading…" : detail?.customer?.name || detail?.customer?.email}
            </DialogTitle>
          </DialogHeader>
          {loadingDetail || !detail ? (
            <div className="p-8 text-center text-neutral-500"><Loader2 className="w-6 h-6 mx-auto animate-spin"/></div>
          ) : (
            <div className="space-y-6" data-testid="customer-detail">
              {/* Top summary */}
              <div className="grid sm:grid-cols-4 gap-3">
                <div className="p-4 rounded-xl bg-neutral-50 border border-neutral-200/70">
                  <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold">Orders</div>
                  <div className="font-display font-bold text-2xl mt-1">{detail.orders_count}</div>
                </div>
                <div className="p-4 rounded-xl bg-neutral-50 border border-neutral-200/70">
                  <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold">Lifetime spend</div>
                  <div className="font-display font-bold text-2xl mt-1">{money(detail.lifetime_spend)}</div>
                </div>
                <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200">
                  <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Gift card</div>
                  <div className="font-display font-bold text-2xl mt-1 text-emerald-800" data-testid="customer-gc-balance">
                    {money(detail.customer.gift_card_balance)}
                  </div>
                </div>
                <div className="p-4 rounded-xl bg-neutral-50 border border-neutral-200/70">
                  <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold">Points (paused)</div>
                  <div className="font-display font-bold text-2xl mt-1 text-neutral-400">{detail.customer.points || 0}</div>
                </div>
              </div>

              {/* Contact */}
              <div className="p-4 rounded-xl bg-white border border-neutral-200/70 grid sm:grid-cols-3 gap-3 text-sm">
                <div><div className="text-xs text-neutral-500">Email</div><div className="font-medium">{detail.customer.email}</div></div>
                <div><div className="text-xs text-neutral-500">Phone</div><div className="font-medium">{detail.customer.phone || "—"}</div></div>
                <div><div className="text-xs text-neutral-500">Joined</div><div className="font-medium">{fmtDate(detail.customer.created_at)}</div></div>
              </div>

              {/* Gift card panel */}
              <div className="p-5 rounded-2xl border border-[hsl(var(--primary))]/25 bg-gradient-to-br from-white to-orange-50/40">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <h3 className="font-display font-semibold text-lg flex items-center gap-2">
                      <Wallet className="w-5 h-5 text-[hsl(var(--primary))]"/> Gift Card Balance
                    </h3>
                    <p className="text-xs text-neutral-500 mt-0.5">Manually add or subtract credit. Every change is logged below.</p>
                  </div>
                  <Button
                    onClick={() => setAdjustOpen(true)}
                    className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90"
                    data-testid="open-adjust-gc"
                  >
                    <Plus className="w-4 h-4 mr-1"/>Adjust balance
                  </Button>
                </div>
                {detail.adjustments.length > 0 && (
                  <div className="mt-4 rounded-xl bg-white border border-neutral-200/70 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500">
                        <tr>
                          <th className="p-3">When</th>
                          <th className="p-3">By</th>
                          <th className="p-3">Reason</th>
                          <th className="p-3 text-right">Change</th>
                          <th className="p-3 text-right">Balance after</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-neutral-100">
                        {detail.adjustments.map((a) => (
                          <tr key={a.id}>
                            <td className="p-3 text-neutral-500 text-xs">{new Date(a.created_at).toLocaleString()}</td>
                            <td className="p-3">{a.adjusted_by_name}</td>
                            <td className="p-3 text-neutral-600">{a.reason || "—"}</td>
                            <td className={`p-3 text-right font-semibold ${a.amount >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                              {a.amount >= 0 ? "+" : "−"}{money(Math.abs(a.amount))}
                            </td>
                            <td className="p-3 text-right">{money(a.balance_after)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {detail.adjustments.length === 0 && (
                  <p className="mt-4 text-sm text-neutral-500 text-center py-6 bg-white rounded-xl border border-neutral-200/70">
                    No adjustments yet.
                  </p>
                )}
              </div>

              {/* Orders */}
              <div>
                <h3 className="font-display font-semibold text-lg mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[hsl(var(--primary))]"/> Recent orders
                </h3>
                {detail.orders.length === 0 ? (
                  <p className="text-sm text-neutral-500 py-6 text-center bg-white rounded-xl border border-neutral-200/70">
                    No orders yet.
                  </p>
                ) : (
                  <div className="rounded-xl bg-white border border-neutral-200/70 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500">
                        <tr>
                          <th className="p-3">Order #</th>
                          <th className="p-3">When</th>
                          <th className="p-3">Store</th>
                          <th className="p-3">Status</th>
                          <th className="p-3 text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-neutral-100">
                        {detail.orders.slice(0, 30).map((o) => (
                          <tr key={o.id}>
                            <td className="p-3 font-mono text-xs">{o.order_number}</td>
                            <td className="p-3 text-neutral-500 text-xs">{new Date(o.created_at).toLocaleString()}</td>
                            <td className="p-3">{o.location || "—"}</td>
                            <td className="p-3"><span className="text-xs">{statusLabel(o.status)}</span></td>
                            <td className="p-3 text-right font-semibold">{money(o.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Adjust dialog */}
      <Dialog open={adjustOpen} onOpenChange={setAdjustOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Adjust Gift Card Balance</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="inline-flex rounded-full border border-neutral-200 bg-white p-1">
                <button
                  onClick={() => setAdjustSign("+")}
                  className={`px-4 h-9 rounded-full text-sm font-medium inline-flex items-center gap-1 ${adjustSign === "+" ? "bg-emerald-500 text-white" : "text-neutral-600"}`}
                  data-testid="adjust-sign-plus"
                ><Plus className="w-4 h-4"/>Credit</button>
                <button
                  onClick={() => setAdjustSign("-")}
                  className={`px-4 h-9 rounded-full text-sm font-medium inline-flex items-center gap-1 ${adjustSign === "-" ? "bg-red-500 text-white" : "text-neutral-600"}`}
                  data-testid="adjust-sign-minus"
                ><Minus className="w-4 h-4"/>Debit</button>
              </div>
            </div>
            <div>
              <Label>Amount (USD)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={adjustAmount}
                onChange={(e) => setAdjustAmount(e.target.value)}
                placeholder="0.00"
                autoFocus
                data-testid="adjust-amount"
              />
            </div>
            <div>
              <Label>Reason (optional)</Label>
              <Textarea
                rows={2}
                value={adjustReason}
                onChange={(e) => setAdjustReason(e.target.value)}
                placeholder="e.g. Refund for order SS-… / birthday gift / adjustment"
                data-testid="adjust-reason"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setAdjustOpen(false)} className="flex-1 rounded-full">
                <X className="w-4 h-4 mr-1"/>Cancel
              </Button>
              <Button
                onClick={submitAdjust}
                disabled={adjustSaving || !adjustAmount}
                className="flex-1 rounded-full bg-[hsl(var(--primary))]"
                data-testid="adjust-save"
              >
                {adjustSaving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin"/>Saving…</> : "Apply"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
